import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Divider,
  Typography,
  Box,
  Paper,
  TextField,
  Grid,
  IconButton,
  Chip,
  Alert,
  Collapse,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Percent as PercentIcon,
  Lock as LockIcon
} from '@mui/icons-material';

const EditEventPolicy = ({ onSave, initialPolicies, isEditable }) => {
  // State for policy sections with default values
  const [policies, setPolicies] = useState(initialPolicies || {
    refundPolicy: {
      title: "Refund Policy",
      description: "Tickets are non-refundable except in case of event cancellation.",
      deadlineDays: 7
    },
    processingFees: {
      title: "Processing Fees",
      description: "Processing fees apply to ticket purchases as set by our company.",
      purchaseFeePercentage: 2.5,
      refundFeePercentage: 1.5,
      absorbFees: false,
      feeDescription: "A processing fee is applied to cover payment processing costs."
    },
    ageRestriction: {
      title: "Age Restriction",
      description: "This event is open to all ages.",
      minAge: null,
      idRequired: false
    },
    behaviorPolicy: {
      title: "Code of Conduct",
      description: "All attendees are expected to behave respectfully toward others.",
      specificRules: [
        "No smoking in designated non-smoking areas",
        "No unauthorized recording of the event"
      ]
    },
    cancellationPolicy: {
      title: "Event Cancellation",
      description: "In case of event cancellation, full refunds will be issued within 30 days. No rescheduling options are available.",
      weatherCancellation: true,
      forceMajeure: true
    },
    mediaPolicy: {
      title: "Photo/Video Policy",
      description: "By attending this event, you consent to being photographed.",
      commercialUse: false,
      optOutOption: true
    }
  });

  const [newRule, setNewRule] = useState('');
  const [saved, setSaved] = useState(false);
  const [expandedSection, setExpandedSection] = useState('refundPolicy');
  const [validationErrors, setValidationErrors] = useState({});

  // Handle section expansion
  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? section : false);
  };

  // Validate policy fields
  const validatePolicies = () => {
    const errors = {};
    
    // Validate refund policy deadline
    if (policies.refundPolicy.deadlineDays < 0) {
      errors.refundPolicy = "Refund deadline cannot be negative";
    }
    
    // Validate age restriction
    if (policies.ageRestriction.minAge !== null) {
      if (policies.ageRestriction.minAge < 0) {
        errors.ageRestriction = "Minimum age cannot be negative";
      } else if (policies.ageRestriction.minAge > 100) {
        errors.ageRestriction = "Please enter a valid minimum age";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle policy text change
  const handlePolicyChange = (policyKey, field, value) => {
    setPolicies(prev => ({
      ...prev,
      [policyKey]: {
        ...prev[policyKey],
        [field]: value
      }
    }));
    
    // Clear validation error when fixing the issue
    if (validationErrors[policyKey]) {
      const newErrors = {...validationErrors};
      delete newErrors[policyKey];
      setValidationErrors(newErrors);
    }
  };

  // Add a new rule to behavior policy
  const addRule = () => {
    if (newRule.trim()) {
      const updatedRules = [...policies.behaviorPolicy.specificRules, newRule.trim()];
      handlePolicyChange('behaviorPolicy', 'specificRules', updatedRules);
      setNewRule('');
    }
  };

  // Remove a rule from behavior policy
  const removeRule = (index) => {
    const updatedRules = policies.behaviorPolicy.specificRules.filter((_, i) => i !== index);
    handlePolicyChange('behaviorPolicy', 'specificRules', updatedRules);
  };

  // Handle save with validation
  const handleSave = () => {
    if (validatePolicies()) {
      if (onSave) onSave(policies);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  // Policy section component using Accordion for better UX
  const PolicySection = ({ 
    title, 
    policyKey, 
    children, 
    error = false,
    helperText 
  }) => {
    return (
      <Accordion 
        expanded={expandedSection === policyKey} 
        onChange={handleAccordionChange(policyKey)}
        sx={{ mb: 2, overflow: 'hidden' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            backgroundColor: 'grey.50',
            borderLeft: error ? '3px solid #d32f2f' : '3px solid transparent'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="h6" sx={{ mr: 2 }}>
              {title}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {error && helperText && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {helperText}
            </Alert>
          )}
          {children}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1 }} /> Event Policies
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Set policies for refunds, processing fees, age restrictions, behavior, and more. Toggle sections to expand.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Collapse in={saved}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Policies saved successfully!
          </Alert>
        </Collapse>

        {/* Refund Policy */}
        <PolicySection 
          title="Refund Policy" 
          policyKey="refundPolicy"
          error={!!validationErrors.refundPolicy}
          helperText={validationErrors.refundPolicy}
        >
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Refund Policy Description"
            value={policies.refundPolicy.description}
            disabled={isEditable}
            onChange={(e) => handlePolicyChange('refundPolicy', 'description', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Refund Deadline (days before event)"
                value={policies.refundPolicy.deadlineDays}
                onChange={(e) => handlePolicyChange('refundPolicy', 'deadlineDays', parseInt(e.target.value) || 0)}
                error={!!validationErrors.refundPolicy}
                inputProps={{ min: 0 }}
                disabled={isEditable}
              />
            </Grid>
          </Grid>
        </PolicySection>

        {/* Processing Fees Policy */}
        <PolicySection 
          title="Processing Fees" 
          policyKey="processingFees"
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Processing Fee Description"
            value={policies.processingFees.feeDescription}
            onChange={(e) => handlePolicyChange('processingFees', 'feeDescription', e.target.value)}
            sx={{ mb: 2 }}
            helperText="This description will be shown to attendees during checkout"
            disabled={isEditable}
          />
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Purchase Processing Fee (%)"
                value={policies.processingFees.purchaseFeePercentage}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Set by company">
                      <LockIcon color="disabled" sx={{ mr: 1 }} />
                    </Tooltip>
                  ),
                }}
                helperText="Set by company policy"
                disabled={isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Refund Processing Fee (%)"
                value={policies.processingFees.refundFeePercentage}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Set by company">
                      <LockIcon color="disabled" sx={{ mr: 1 }} />
                    </Tooltip>
                  ),
                }}
                helperText="Set by company policy"
                disabled={isEditable}
              />
            </Grid>
          </Grid>
          
          <FormControlLabel
            control={
              <Switch
                checked={policies.processingFees.absorbFees}
                onChange={(e) => handlePolicyChange('processingFees', 'absorbFees', e.target.checked)}
                color="primary"
                disabled={isEditable}
              />
            }
            label={
              <Box>
                <Typography>Absorb processing fees</Typography>
                <Typography variant="caption" color="text.secondary">
                  {policies.processingFees.absorbFees 
                    ? "You will cover all processing fees instead of passing them to attendees" 
                    : "Processing fees will be passed to attendees during purchase and deducted from refunds"}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />
          
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Fee Calculation Example:</strong> 
              {policies.processingFees.absorbFees ? 
                " You will cover the processing fees for all transactions." : 
                ` For a $100 ticket, attendees will pay $${(100 + (100 * policies.processingFees.purchaseFeePercentage / 100)).toFixed(2)} ` +
                `($${100} + $${(100 * policies.processingFees.purchaseFeePercentage / 100).toFixed(2)} fee). ` +
                `If refunded, they will receive $${(100 - (100 * policies.processingFees.refundFeePercentage / 100)).toFixed(2)}.`
              }
            </Typography>
          </Alert>
        </PolicySection>

        {/* Age Restriction Policy */}
        <PolicySection 
          title="Age Restrictions" 
          policyKey="ageRestriction"
          error={!!validationErrors.ageRestriction}
          helperText={validationErrors.ageRestriction}
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Age Policy Description"
            value={policies.ageRestriction.description}
            onChange={(e) => handlePolicyChange('ageRestriction', 'description', e.target.value)}
            sx={{ mb: 2 }}
            disabled={isEditable}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Age (leave empty if no restriction)"
                value={policies.ageRestriction.minAge || ''}
                onChange={(e) => handlePolicyChange('ageRestriction', 'minAge', e.target.value ? parseInt(e.target.value) : null)}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                error={!!validationErrors.ageRestriction}
                disabled={isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={policies.ageRestriction.idRequired}
                    onChange={(e) => handlePolicyChange('ageRestriction', 'idRequired', e.target.checked)}
                    color="primary"
                    disabled={isEditable}
                  />
                }
                label="Require ID Verification"
              />
            </Grid>
          </Grid>
        </PolicySection>

        {/* Behavior Policy */}
        <PolicySection title="Code of Conduct" policyKey="behaviorPolicy">
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Behavior Policy Description"
            value={policies.behaviorPolicy.description}
            onChange={(e) => handlePolicyChange('behaviorPolicy', 'description', e.target.value)}
            sx={{ mb: 2 }}
            disabled={isEditable}
          />
          
          <Typography variant="subtitle2" gutterBottom>
            Specific Rules:
          </Typography>
          
          <Box sx={{ display: 'flex', mb: 2 }}>
            <TextField
              fullWidth
              label="Add a specific rule"
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addRule()}
              sx={{ mr: 1 }}
              disabled={isEditable}
            />
            <Tooltip title="Add Rule">
              <Button 
                variant="contained" 
                onClick={addRule}
                disabled={!newRule.trim()}
              >
                <AddIcon />
              </Button>
            </Tooltip>
          </Box>
          
          {policies.behaviorPolicy.specificRules.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {policies.behaviorPolicy.specificRules.map((rule, index) => (
                <Chip
                  key={index}
                  label={rule}
                  onDelete={() => removeRule(index)}
                  color="primary"
                  variant="outlined"
                  deleteIcon={<DeleteIcon />}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No specific rules added yet.
            </Typography>
          )}
        </PolicySection>

        {/* Cancellation Policy */}
        <PolicySection title="Cancellation Policy" policyKey="cancellationPolicy">
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Policy Description"
            value={policies.cancellationPolicy.description}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
            helperText="This policy is fixed and cannot be edited"
            disabled={isEditable}
          />
          <FormControlLabel
            control={
              <Switch
                checked={policies.cancellationPolicy.weatherCancellation}
                onChange={(e) => handlePolicyChange('cancellationPolicy', 'weatherCancellation', e.target.checked)}
                color="primary"
                disabled={isEditable}
              />
            }
            label="Allow cancellation due to weather"
          />
          <FormControlLabel
            control={
              <Switch
                checked={policies.cancellationPolicy.forceMajeure}
                onChange={(e) => handlePolicyChange('cancellationPolicy', 'forceMajeure', e.target.checked)}
                color="primary"
                disabled={isEditable}
              />
            }
            label="Include force majeure clause"
            sx={{ display: 'block', mt: 1 }}
          />
        </PolicySection>

        {/* Media Policy */}
        <PolicySection title="Media Policy" policyKey="mediaPolicy">
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Media Policy Description"
            value={policies.mediaPolicy.description}
            onChange={(e) => handlePolicyChange('mediaPolicy', 'description', e.target.value)}
            sx={{ mb: 2 }}
            disabled={isEditable}
          />
          <FormControlLabel
            control={
              <Switch
                checked={policies.mediaPolicy.commercialUse}
                onChange={(e) => handlePolicyChange('mediaPolicy', 'commercialUse', e.target.checked)}
                color="primary"
                disabled={isEditable}
              />
            }
            label="Allow commercial use of photos/videos"
          />
          <FormControlLabel
            control={
              <Switch
                checked={policies.mediaPolicy.optOutOption}
                onChange={(e) => handlePolicyChange('mediaPolicy', 'optOutOption', e.target.checked)}
                color="primary"
                disabled={isEditable}
              />
            }
            label="Provide opt-out option for attendees"
            sx={{ display: 'block', mt: 1 }}
          />
        </PolicySection>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />} 
            size="large"
            onClick={handleSave}
            disabled={Object.keys(validationErrors).length > 0 || isEditable}
            sx={{ minWidth: 200 }}
          >
            Save All Policies
          </Button>
        </Box>
        
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please fix the validation errors before saving.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EditEventPolicy;